// GestMob Data Pre-caching Service
// Pre-loads essential data for offline use

import { offlineStorage, formatCacheAge } from './offlineStorage';
import { furnitureApi, locationsApi, catalogApi, furnitureTypesApi, inventoryApi } from './api';

// TTL values in milliseconds
const TTL = {
  SHORT: 5 * 60 * 1000,      // 5 minutes
  MEDIUM: 30 * 60 * 1000,    // 30 minutes
  LONG: 24 * 60 * 60 * 1000, // 24 hours
  STATIC: 7 * 24 * 60 * 60 * 1000 // 7 days
};

// Endpoints to pre-cache with their configurations
const CACHE_CONFIG = {
  '/api/furniture-types': {
    fetcher: () => furnitureTypesApi.getAll(),
    ttl: TTL.LONG,
    priority: 1,
    label: 'Types de mobilier'
  },
  '/api/catalog': {
    fetcher: () => catalogApi.getAll(),
    ttl: TTL.LONG,
    priority: 2,
    label: 'Catalogue'
  },
  '/api/locations/tree': {
    fetcher: () => locationsApi.getTree(),
    ttl: TTL.LONG,
    priority: 3,
    label: 'Arborescence complète'
  },
  '/api/locations/sites': {
    fetcher: () => locationsApi.getSites(),
    ttl: TTL.LONG,
    priority: 4,
    label: 'Sites'
  },
  '/api/furniture': {
    fetcher: () => furnitureApi.getAll({ limit: 100000 }),
    ttl: TTL.MEDIUM,
    priority: 5,
    label: 'Mobilier'
  },
  '/api/inventory': {
    fetcher: () => inventoryApi.getAll({ limit: 100000 }),
    ttl: TTL.MEDIUM,
    priority: 6,
    label: 'Inventaires'
  }
};

class CacheManager {
  constructor() {
    this.cacheStatus = {};
    this.isPreCaching = false;
    this.listeners = new Set();
  }

  // Subscribe to cache status changes
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notifyListeners() {
    this.listeners.forEach(cb => cb(this.getCacheStatus()));
  }

  // Get current cache status
  getCacheStatus() {
    return {
      isPreCaching: this.isPreCaching,
      endpoints: this.cacheStatus
    };
  }

  // Pre-cache all essential data
  async preCacheAll(onProgress) {
    if (this.isPreCaching) {
      console.log('[CacheManager] Pre-caching already in progress');
      return;
    }

    this.isPreCaching = true;
    this.notifyListeners();

    const endpoints = Object.entries(CACHE_CONFIG)
      .sort(([, a], [, b]) => a.priority - b.priority);

    let completed = 0;
    const total = endpoints.length;

    console.log(`[CacheManager] Pre-caching ${total} endpoints...`);

    for (const [endpoint, config] of endpoints) {
      try {
        this.cacheStatus[endpoint] = { status: 'loading', label: config.label };
        this.notifyListeners();

        const response = await config.fetcher();
        await offlineStorage.cacheData(endpoint, response.data, { ttl: config.ttl });
        await offlineStorage.setLastSync(endpoint);

        this.cacheStatus[endpoint] = { 
          status: 'cached', 
          label: config.label,
          cachedAt: Date.now(),
          itemCount: Array.isArray(response.data) ? response.data.length : 1
        };

        completed++;
        onProgress?.({ completed, total, endpoint, label: config.label });
        console.log(`[CacheManager] Cached ${endpoint} (${completed}/${total})`);

      } catch (error) {
        console.error(`[CacheManager] Failed to cache ${endpoint}:`, error);
        this.cacheStatus[endpoint] = { 
          status: 'error', 
          label: config.label,
          error: error.message 
        };
      }
    }

    this.isPreCaching = false;
    this.notifyListeners();

    console.log('[CacheManager] Pre-caching complete');
    return this.getCacheStatus();
  }

  // Pre-cache with site filter (for limited offline mode)
  async preCacheWithFilter(filters = {}, onProgress) {
    if (this.isPreCaching) {
      console.log('[CacheManager] Pre-caching already in progress');
      return;
    }

    this.isPreCaching = true;
    this.notifyListeners();

    // Configuration avec filtres pour les endpoints qui supportent le filtrage
    const filteredConfig = {
      '/api/furniture-types': {
        fetcher: () => furnitureTypesApi.getAll(),
        ttl: TTL.LONG,
        priority: 1,
        label: 'Types de mobilier'
      },
      '/api/catalog': {
        fetcher: () => catalogApi.getAll(),
        ttl: TTL.LONG,
        priority: 2,
        label: 'Catalogue'
      },
      '/api/locations/tree': {
        fetcher: () => locationsApi.getTree(),
        ttl: TTL.LONG,
        priority: 3,
        label: 'Arborescence complète'
      },
      '/api/locations/sites': {
        fetcher: () => locationsApi.getSites(),
        ttl: TTL.LONG,
        priority: 4,
        label: 'Sites'
      },
      '/api/furniture': {
        fetcher: () => furnitureApi.getAll({ ...filters, limit: 100000 }),
        ttl: TTL.MEDIUM,
        priority: 5,
        label: filters.site_id ? 'Mobilier (filtré par site)' : 'Mobilier'
      },
      '/api/inventory': {
        fetcher: () => inventoryApi.getAll({ ...filters, limit: 100000 }),
        ttl: TTL.MEDIUM,
        priority: 6,
        label: filters.site_id ? 'Inventaires (filtré par site)' : 'Inventaires'
      }
    };

    const endpoints = Object.entries(filteredConfig)
      .sort(([, a], [, b]) => a.priority - b.priority);

    let completed = 0;
    const total = endpoints.length;

    console.log(`[CacheManager] Pre-caching ${total} endpoints with filters:`, filters);

    for (const [endpoint, config] of endpoints) {
      try {
        this.cacheStatus[endpoint] = { status: 'loading', label: config.label };
        this.notifyListeners();

        const response = await config.fetcher();
        await offlineStorage.cacheData(endpoint, response.data, { ttl: config.ttl });
        await offlineStorage.setLastSync(endpoint);

        this.cacheStatus[endpoint] = { 
          status: 'cached', 
          label: config.label,
          cachedAt: Date.now(),
          itemCount: Array.isArray(response.data) ? response.data.length : 1
        };

        completed++;
        onProgress?.({ completed, total, endpoint, label: config.label });
        console.log(`[CacheManager] Cached ${endpoint} (${completed}/${total}) - ${Array.isArray(response.data) ? response.data.length : 1} items`);

      } catch (error) {
        console.error(`[CacheManager] Failed to cache ${endpoint}:`, error);
        this.cacheStatus[endpoint] = { 
          status: 'error', 
          label: config.label,
          error: error.message 
        };
      }
    }

    this.isPreCaching = false;
    this.notifyListeners();

    console.log('[CacheManager] Filtered pre-caching complete');
    return this.getCacheStatus();
  }

  // Cache a specific endpoint
  async cacheEndpoint(endpoint, data, options = {}) {
    const config = CACHE_CONFIG[endpoint] || { ttl: TTL.MEDIUM, label: endpoint };
    await offlineStorage.cacheData(endpoint, data, { ttl: config.ttl, ...options });
    await offlineStorage.setLastSync(endpoint);
    
    this.cacheStatus[endpoint] = {
      status: 'cached',
      label: config.label,
      cachedAt: Date.now(),
      itemCount: Array.isArray(data) ? data.length : 1
    };
    
    this.notifyListeners();
  }

  // Get cached data for an endpoint
  async getCachedData(endpoint) {
    const cached = await offlineStorage.getCachedData(endpoint);
    if (cached) {
      return {
        data: cached.data,
        cachedAt: cached.cachedAt,
        age: formatCacheAge(cached.cachedAt),
        isStale: cached.expiresAt ? Date.now() > cached.expiresAt : false
      };
    }
    return null;
  }

  // Get data with fallback to cache
  async getWithCache(endpoint, fetcher) {
    // Try network first
    if (navigator.onLine) {
      try {
        const response = await fetcher();
        await this.cacheEndpoint(endpoint, response.data);
        return {
          data: response.data,
          fromCache: false,
          cachedAt: Date.now()
        };
      } catch (error) {
        console.log(`[CacheManager] Network failed for ${endpoint}, trying cache...`);
      }
    }

    // Fallback to cache
    const cached = await this.getCachedData(endpoint);
    if (cached) {
      return {
        data: cached.data,
        fromCache: true,
        cachedAt: cached.cachedAt,
        age: cached.age
      };
    }

    throw new Error('Données non disponibles hors ligne');
  }

  // Get cache freshness info for all endpoints
  async getCacheFreshness() {
    const freshness = {};
    
    for (const [endpoint, config] of Object.entries(CACHE_CONFIG)) {
      const lastSync = await offlineStorage.getLastSync(endpoint);
      const cached = await offlineStorage.getCachedData(endpoint);
      
      freshness[endpoint] = {
        label: config.label,
        lastSync,
        age: formatCacheAge(lastSync),
        isCached: !!cached,
        itemCount: cached ? (Array.isArray(cached.data) ? cached.data.length : 1) : 0,
        isStale: lastSync ? (Date.now() - lastSync) > config.ttl : true
      };
    }
    
    return freshness;
  }

  // Clear all cached data
  async clearAll() {
    await offlineStorage.clearCache();
    this.cacheStatus = {};
    this.notifyListeners();
  }

  // Refresh stale caches
  async refreshStale() {
    const freshness = await this.getCacheFreshness();
    
    for (const [endpoint, info] of Object.entries(freshness)) {
      if (info.isStale && navigator.onLine) {
        const config = CACHE_CONFIG[endpoint];
        if (config) {
          try {
            const response = await config.fetcher();
            await this.cacheEndpoint(endpoint, response.data);
            console.log(`[CacheManager] Refreshed stale cache: ${endpoint}`);
          } catch (error) {
            console.error(`[CacheManager] Failed to refresh ${endpoint}:`, error);
          }
        }
      }
    }
  }
}

// Singleton instance
export const cacheManager = new CacheManager();
export { formatCacheAge };
export default cacheManager;
